import React from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mesh.ai/rabbit';
import { L2_VALUES } from 'Deer/constants/routes';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { QuestionLibrary } from 'Deer/pages/Company/Engagement/Questions/QuestionLibrary';
import { SurveyTab } from 'Deer/pages/Company/Engagement/Surveys';
import { TemplatesTab } from 'Deer/pages/Company/Engagement/Templates';
import { useQuery } from 'components/hooks/useQuery';
import { useHistory } from 'react-router-dom';
import Competencies from 'screens/admin/Competencies';
import { CompetencySettings } from 'screens/admin/Competencies/CompetencySettings/CompetencySettings';
import { ReviewsList } from 'screens/admin/Reviews/ReviewProcessV1/ReviewsList/ReviewsList';
import ReviewQuestionBankV1 from 'screens/admin/Reviews/ReviewQuestionBankV1/ReviewQuestionBankV1';
import ReviewTemplateListWrapperV1 from 'screens/admin/Reviews/ReviewTemplateListV1';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { sendClickedOnPreferencesTabEvent } from 'constants/mixpanelHelpers/genericEvents';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';

import { PlainTextHeader } from '../HeaderComponents/PlainTextHeader';

const MODULE_SETTINGS_TABS = {
  [L2_VALUES.review]: [
    {
      name: 'Review Cycles',
      queryParamId: 'review_cycles',
      Comp: ReviewsList,
    },
    {
      name: 'Templates',
      queryParamId: 'templates',
      Comp: ReviewTemplateListWrapperV1,
    },
    {
      name: 'Question Library',
      queryParamId: 'question_library',
      Comp: ReviewQuestionBankV1,
    },
  ],

  [L2_VALUES.engagement]: [
    // {
    //   name: 'Drivers',
    //   queryParamId: 'engagement_drivers',
    //   Comp: Drivers,
    // },
    {
      name: 'Surveys',
      queryParamId: 'engagement_surveys',
      Comp: () => <SurveyTab />,
    },
    {
      name: 'Templates',
      queryParamId: 'templates',
      Comp: () => <TemplatesTab />,
    },
    {
      name: 'Question Library',
      queryParamId: 'question_library',
      Comp: () => <QuestionLibrary />,
    },
  ],
  [L2_VALUES.develop]: [
    {
      name: 'Competency Library',
      queryParamId: 'competency_library',
      Comp: Competencies,
    },
    {
      name: 'Competency Settings',
      queryParamId: 'competency_settings',
      Comp: CompetencySettings,
    },
  ],
};

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerLabel?: string;
}

const TabbedModuleSettings = ({ headerLabel = '' }: Props) => {
  const history = useHistory();
  const { activeTabId } = useQuery();
  const { l3Options: tabsDeerCompanyLevel, l2Module } = useRouteToNavLevels();

  const onTabChangeHandler = (newActiveTab: string) => {
    sendClickedOnPreferencesTabEvent({
      eventProperties: {
        activeTabId,
        source: l2Module,
      },
    });
    history.push({
      search: `?${new URLSearchParams({
        activeTabId: newActiveTab,
      }).toString()}`,
    });
  };

  const allowedSettingsTabs = MODULE_SETTINGS_TABS[l2Module];

  if (!activeTabId) {
    onTabChangeHandler(allowedSettingsTabs[0].queryParamId);
  }

  return (
    <>
      {headerLabel && <PlainTextHeader>{headerLabel}</PlainTextHeader>}
      {!checkIntentLandingPagesFeatureEnabled() && <PageTabbedNavigationV1 tabs={tabsDeerCompanyLevel} />}

      <Tabs variant="secondary" value={activeTabId as string} onValueChange={onTabChangeHandler} className="mt-1">
        <TabsList>
          {allowedSettingsTabs.map(({ name, queryParamId }) => (
            <TabsTrigger key={queryParamId} value={queryParamId}>
              {name}
            </TabsTrigger>
          ))}
        </TabsList>

        {allowedSettingsTabs.map(({ Comp, queryParamId }) => (
          <TabsContent key={queryParamId} value={queryParamId}>
            <Comp />
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
};

export { TabbedModuleSettings };
